import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, Sparkles, Check, Video, Users, Percent } from "lucide-react";
import Reveal from "@/components/Reveal";

const inclusi = [
  "DJ e animazione",
  "Location in esclusiva",
  "Diritti SIAE",
  "Buffet dall'aperitivo alla cena",
  "Torta e dolci, bevande incluse",
  "Allestimento personalizzato del locale",
  "Staff dedicato per gestire l'evento",
];

const omaggi = [
  { icon: Video, title: "Video invito", text: "Un video invito personalizzato da inviare ai tuoi ospiti." },
  { icon: Users, title: "5 ospiti in più", text: "Cinque persone in più oltre le 40, con buffet in omaggio." },
  { icon: Percent, title: "30% sui servizi extra", text: "Uno sconto del 30% su tutti i nostri servizi extra." },
];

function Corner({ className }) {
  return <span className={`pointer-events-none absolute h-8 w-8 border-dusty/70 ${className}`} aria-hidden="true" />;
}

export default function Promo() {
  const [open, setOpen] = useState(false);

  return (
    <section id="promo" data-testid="promo-section" className="relative overflow-hidden bg-midnight px-6 py-28 text-linen md:py-36 lg:px-10">
      <div className="pointer-events-none absolute -left-40 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-sand/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-dusty/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl">
        <Reveal>
          <div className="relative border border-white/15 bg-deepblue/80 p-8 backdrop-blur-md sm:p-12 lg:p-16">
            <Corner className="left-3 top-3 border-l border-t" />
            <Corner className="right-3 top-3 border-r border-t" />
            <Corner className="bottom-3 left-3 border-b border-l" />
            <Corner className="bottom-3 right-3 border-b border-r" />

            <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
              <div>
                <p className="mb-6 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.35em] text-dusty" data-testid="promo-tag">
                  <Sparkles className="h-4 w-4" /> Promozione infrasettimanale
                </p>
                <h2 className="font-serif text-4xl leading-tight tracking-tight sm:text-5xl md:text-6xl">
                  Il tuo evento, <span className="italic text-dusty">tutto incluso</span>.
                </h2>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.3em] text-linen/60" data-testid="promo-days">
                  Dal lunedì al giovedì
                </p>
                <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-linen/70 md:text-lg">
                  Un pacchetto completo pensato per chi vuole festeggiare senza
                  pensieri: dalla musica al buffet, dall'allestimento allo staff,
                  ci occupiamo noi di ogni dettaglio.
                </p>

                <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="promo-inclusi">
                  {inclusi.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-light text-linen/85 md:text-base">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-dusty" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col justify-between border-t border-white/15 pt-10 lg:border-l lg:border-t-0 lg:pl-16 lg:pt-0">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.35em] text-linen/50">Pacchetto completo</p>
                  <p className="mt-4 font-serif text-6xl leading-none tracking-tight text-linen sm:text-7xl" data-testid="promo-price">
                    1.750<span className="ml-2 text-3xl text-dusty sm:text-4xl">€</span>
                  </p>
                  <p className="mt-3 text-sm font-light text-linen/60" data-testid="promo-persone">per 40 persone</p>

                  <button
                    data-testid="promo-cta-button"
                    onClick={() => window.scrollToSection?.("#contatti")}
                    className="mt-8 inline-flex w-full items-center justify-center bg-sand px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-linen transition-colors duration-300 hover:bg-dusty sm:w-auto"
                  >
                    Richiedi il pacchetto
                  </button>
                </div>

                <div className="mt-10">
                  <p className="text-sm font-light italic leading-relaxed text-linen/60">
                    E per chi sceglie di festeggiare con noi, abbiamo riservato tre piccole sorprese…
                  </p>
                  <button
                    data-testid="promo-omaggi-toggle"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    className="mt-4 inline-flex items-center gap-3 border-b border-dusty/60 pb-1 text-xs font-semibold uppercase tracking-[0.25em] text-dusty transition-colors duration-300 hover:border-linen hover:text-linen"
                  >
                    <Gift className="h-4 w-4" /> {open ? "Nascondi gli omaggi" : "Scopri gli omaggi"}
                  </button>

                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.ul
                        key="omaggi"
                        data-testid="promo-omaggi-list"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        {omaggi.map((o, i) => (
                          <motion.li
                            key={o.title}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15 + i * 0.12, duration: 0.5 }}
                            className="mt-5 flex items-start gap-4"
                            data-testid={`promo-omaggio-${i + 1}`}
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-dusty/40 text-dusty">
                              <o.icon className="h-4 w-4" />
                            </span>
                            <div>
                              <p className="font-serif text-xl text-linen">{o.title}</p>
                              <p className="mt-1 text-sm font-light leading-relaxed text-linen/60">{o.text}</p>
                            </div>
                          </motion.li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
